"use client";

import React, { useMemo } from "react";
import {
  GridTable,
  type GridTableColumn,
} from "@/src/components/StockDetails/GridTable";
import { useStockDetailsMarket } from "@/src/hooks";
import {
  formatDividendsDate,
  formatDividendsNumberData,
  formatPayFrequency,
} from "@/src/components/StockDetails/StockDividends/DividendsUtils";
import { ViewDetailsLink, buildStockDetailModuleHref } from "./ViewDetailsLink";

interface AssetDividendInfoBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetDividendInfoBusinessChart: React.FC<
  AssetDividendInfoBusinessChartProps
> = ({ assetRouterId }) => {
  const { data: stockData, isLoading } = useStockDetailsMarket({
    queryParams: {
      ids: [assetRouterId],
      "enrich.enable": true,
      "enrich.with_dividends.enable": true,
      "enrich.with_dividends.limit": 5,
    },
    queryKey: ["chat-dividend-info", assetRouterId],
  });

  const stockSymbol = stockData?.symbol || "";

  const dataSource: GridTableColumn[][] = useMemo(() => {
    const dividends = stockData?.dividends;
    const currentDividend = dividends?.items?.[0];

    return [
      [
        {
          label: "Dividend Yield",
          value: `${formatDividendsNumberData(dividends?.dividend_yield_ttm || 0, 2)}%`,
        },
        {
          label: "Annual Dividend",
          value: `$${formatDividendsNumberData(dividends?.dividend_per_share_annual || 0)}`,
        },
        {
          label: "Ex-Dividend Date",
          value: formatDividendsDate(currentDividend?.ex_dividend_date || ""),
        },
      ],
      [
        {
          label: "Payout Frequency",
          value: formatPayFrequency(currentDividend?.pay_freq) || "",
        },
        {
          label: "Payout Ratio",
          value: `${formatDividendsNumberData(dividends?.payout_ratio_ttm || 0, 2)}%`,
        },
        { label: "", value: "" },
      ],
    ];
  }, [stockData]);

  if (isLoading) {
    return (
      <div className="h-40 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!stockData?.dividends?.items?.length) {
    return null;
  }

  return (
    <>
      <div className="w-full overflow-hidden">
        <GridTable dataSource={dataSource} />
      </div>
      {stockSymbol && (
        <ViewDetailsLink
          href={buildStockDetailModuleHref(stockSymbol, "dividends")}
        />
      )}
    </>
  );
};
